import { useState } from 'react';
import { addMonths, isAfter, isPast } from 'date-fns';

import {
  Box,
  Button,
  ButtonGroup,
  DateInput,
  DatePicker,
  FormField,
  FormHelp,
  Label,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

import userPersonas from '@common/constants/userPersonas';
import Modal from '@common/components/ConstellationV10Modal';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import { validateDueDateInputs } from '../validation';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';

interface Props {
  saveStateToReduxStore: (e) => Promise<any>;
  onCancel: () => void;
  onSubmit: (editMode: boolean) => Record<string, any>;
  dueDate: string;
  editMode: boolean;
  fromDashboard: boolean;
  goToConfirmation: () => void;
  goToExperianFlow: () => void;
  setupNewTerm: boolean;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  onClose: () => void;
  togglePrevPage: () => void;
}

const DueDatePage = ({
  saveStateToReduxStore,
  onCancel,
  onSubmit,
  dueDate: initialDueDate,
  editMode = false,
  fromDashboard = false,
  goToConfirmation,
  goToExperianFlow,
  setupNewTerm = false,
  currentProperty,
  user,
  onClose,
  togglePrevPage,
}: Props) => {
  const [dueDate, setDueDate] = useState(initialDueDate === '' ? '' : formatDate(initialDueDate, 'MM/dd/yyyy'));
  const [errors, setErrors] = useState({ emptyError: '', pastDueDateError: '', pastCutoffDateError: '' });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const cutoffDate = addMonths(new Date(), 2);
  const checkDateValid = (i: number | Date): boolean => i <= cutoffDate;

  const handleUpdate = async (): Promise<any> => {
    const validInputs = validateDueDateInputs({ dueDate, handleErrors: setErrors });

    if (!validInputs) {
      return;
    }

    IreneOnboardingCSEvents.emitContinueFromMICDueDateEvent({
      currentProperty,
      user,
    });

    await saveStateToReduxStore({
      dueDate: formatDate(dueDate, 'yyyy-MM-dd'),
    });
    const result = await onSubmit(editMode);
    if (setupNewTerm) {
      if (result && result.success) {
        if (result.experianFlowRequired) {
          goToExperianFlow();
        } else {
          goToConfirmation();
        }
      }
    }
  };

  const renderMainSection = () => (
    <Box>
      <Paragraph css={{ marginBottom: 'default' }} textStyle="body-lg-bold">
        When are your move-in related costs due?
      </Paragraph>
      <FormField
        disabled={!checkDateValid}
        error={!!(errors.emptyError || errors.pastDueDateError || errors.pastCutoffDateError)}
        description={
          errors.emptyError || errors.pastDueDateError || errors.pastCutoffDateError ? (
            <FormHelp>{errors.emptyError || errors.pastDueDateError || errors.pastCutoffDateError}</FormHelp>
          ) : (
            <FormHelp>
              Your renter is charged when they send payment. The funds reach your account a few days later.
            </FormHelp>
          )
        }
        label={<Label>Due date</Label>}
      >
        <DatePicker
          closeModalOnDateSelect
          dateFormat="MM/dd/yyyy"
          disabledDates={(date) => isPast(date) || isAfter(date, cutoffDate)}
          onOpenChange={(open) => setIsCalendarOpen(open)}
          open={isCalendarOpen}
          input={
            <DateInput
              css={{ paddingLeft: 'sm' }}
              value={dueDate}
              readOnly
              onClick={() => setIsCalendarOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setIsCalendarOpen(true);
                }
              }}
            />
          }
          onValueChange={(value) => {
            setDueDate(String(value));
            setErrors({ emptyError: '', pastDueDateError: '', pastCutoffDateError: '' });
          }}
          value={dueDate}
        />
      </FormField>
    </Box>
  );

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={onClose}
      header={fromDashboard ? 'Move-in costs' : 'Payment setup'}
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          {renderMainSection()}
          <FaqComponent faqs={FaqConstants.ONBOARDING_V2.MOVE_IN_COSTS.DUE_DATE_PAGE} />
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Move-in costs due date actions">
          <TextButton onClick={togglePrevPage}>Back</TextButton>
          <Button emphasis="filled" onClick={handleUpdate}>
            {editMode && !fromDashboard ? 'Update' : 'Continue'}
          </Button>
          {editMode && !fromDashboard && (
            <Button emphasis="outlined" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </ButtonGroup>
      }
    />
  );
};

export default DueDatePage;
